import { Box, Icon, keyframes, Link, Text, VStack } from '@chakra-ui/react';
import { JobEntity } from '@shared/db';
import { TASKS_ROUTE } from '@web-app/components/consts';
import { BerryLogo } from '@web-app/components/Daily/Tray/Icons';
import { FC } from 'react';

export interface PostCallPanelV2Props {
  job: JobEntity | null;
  isPending: boolean;
  error: string | null;
  duration: number;
  willGenerateSoapNotes: boolean;
}

const spin = keyframes`
  from {transform: rotate(0deg);}
  to {transform: rotate(360deg)}
`;

export const PostCallPanelV2: FC<PostCallPanelV2Props> = ({
  job,
  isPending,
  error,
  duration,
  willGenerateSoapNotes,
}) => {
  // Skipped state - appointment status was not "Occurred", no SOAP note generation
  if (!willGenerateSoapNotes) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        bg="branded.light.bg.default"
        borderWidth={3}
        borderColor="white"
        borderRadius={10}
        textAlign="center"
        overflow="hidden"
        color="branded.light.text.default"
        p={8}
        h="100%"
      >
        <VStack spacing={6}>
          <Text fontSize="60px" lineHeight={1}>
            👋
          </Text>
          <Box>
            <Text fontSize="25px" fontWeight="bold" mb={2}>
              The session has ended
            </Text>
            <Text fontSize="md" color="text.light">
              Have a nice day!
            </Text>
          </Box>
        </VStack>
      </Box>
    );
  }

  // Loading state - SOAP note generation in progress
  if (isPending) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        bg="branded.light.bg.default"
        borderWidth={3}
        borderColor="white"
        borderRadius={10}
        textAlign="center"
        overflow="hidden"
        p={8}
        h="100%"
      >
        <Icon
          color="branded.light.text.default"
          boxSize={{ md: 300, base: 100 }}
          animation={`${spin} 2s infinite`}
        >
          <BerryLogo />
        </Icon>
      </Box>
    );
  }

  // Error state - SOAP note generation failed
  if (error || !job) {
    return (
      <Box
        color="branded.light.text.default"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        textAlign="center"
        alignItems="center"
        bg="branded.light.bg.default"
        borderWidth={3}
        borderColor="white"
        borderRadius={10}
        overflow="auto"
        p={8}
        h="100%"
      >
        <Text>
          There was an error generating the SOAP note. Please reach out to{' '}
          <a href="mailto:help@berrystreet.co?subject=Error Generating SOAP Note">
            help@berrystreet.co
          </a>{' '}
          for assistance.
        </Text>
        {error && (
          <Text fontSize="sm" color="branded.light.text.muted" mt={2}>
            Error: {error}
          </Text>
        )}
      </Box>
    );
  }

  // Success state - Job queued successfully
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexDirection="column"
      bg="branded.light.bg.default"
      borderWidth={3}
      borderColor="white"
      borderRadius={10}
      textAlign="center"
      overflow="hidden"
      color="branded.light.text.default"
      p={8}
      h="100%"
    >
      <VStack spacing={4}>
        <Text fontSize="2xl" fontWeight="bold" lineHeight={1.2}>
          Generating automated session summary
        </Text>
        <Text color="text.light" fontSize="md">
          This process can take 30-90 seconds. Visit the{' '}
          <Link
            href={TASKS_ROUTE}
            textDecoration="underline"
            variant="outline"
            borderColor="branded.light.border.default"
          >
            tasks
          </Link>{' '}
          page to review and approve your session notes. Once you submit your
          reviewed notes, they&apos;ll be sent to Healthie.
        </Text>
        {duration > 0 && (
          <Text color="text.light" fontSize="sm" mt={2}>
            Session duration: {duration} minutes
          </Text>
        )}
      </VStack>
    </Box>
  );
};
